import React, { useEffect, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import type { PendingPaymentClientGroup } from "../../../types";
import { formatDateShort, formatTime } from "../../../utils/dateUtils";
import { Card, EmptyState, ErrorState, Icons, Loading } from "../../shared";
import { BalanceUnpaidRow } from "./BalanceUnpaidRow";
import { formatBalanceCurrency } from "./balanceShared";

function parsePrice(price: number | string): number {
  if (typeof price === "number") return price;
  return parseFloat(price) || 0;
}

interface PendingPaymentsPanelProps {
  clients: PendingPaymentClientGroup[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onPay: (appointmentId: number, group: PendingPaymentClientGroup) => void;
}

export const PendingPaymentsPanel: React.FC<PendingPaymentsPanelProps> = ({
  clients,
  loading,
  error,
  onRetry,
  onPay,
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const visible = new Set(clients.map((group) => group.client.id));
    setExpanded((prev) => {
      const next = new Set<number>();
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
      });
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [clients]);

  if (loading) {
    return (
      <Card className="p-8 border border-gray-100 shadow-sm">
        <Loading message="Carregando pagamentos pendentes…" size="md" className="py-8" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 border border-gray-100 shadow-sm">
        <ErrorState message={error} variant="inline" onRetry={onRetry} className="py-4" />
      </Card>
    );
  }

  if (clients.length === 0) {
    return (
      <Card className="p-8 border border-gray-100 shadow-sm">
        <EmptyState
          icon={<Icons.Payment sx={{ fontSize: 56 }} />}
          title="Nenhum pagamento pendente"
          description="Todos os agendamentos deste ano já têm forma de pagamento registrada."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {clients.map((group) => {
        const isOpen = expanded.has(group.client.id);
        return (
          <Card
            key={group.client.id}
            className="border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            <button
              type="button"
              onClick={() =>
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(group.client.id)) next.delete(group.client.id);
                  else next.add(group.client.id);
                  return next;
                })
              }
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-800/80 transition-colors"
              aria-expanded={isOpen}
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-white truncate">
                  {group.client.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {group.count} {group.count === 1 ? "agendamento" : "agendamentos"}
                  {group.client.phoneNumber ? ` · ${group.client.phoneNumber}` : ""}
                </p>
              </div>
              <span className="text-sm sm:text-base font-semibold text-red-700 dark:text-red-400 flex-shrink-0">
                {formatBalanceCurrency(group.total)}
              </span>
              <ExpandMore
                sx={{
                  fontSize: 22,
                  color: "inherit",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
                className="text-gray-500 dark:text-slate-400 flex-shrink-0"
              />
            </button>

            {isOpen && (
              <div className="px-4 pb-4 space-y-2 border-t border-gray-100 dark:border-slate-700 pt-3">
                {group.appointments.map((row) => (
                  <BalanceUnpaidRow
                    key={row.id}
                    onPay={() => onPay(row.id, group)}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-gray-600 dark:text-slate-400">
                        {formatDateShort(row.appointmentDate)}
                      </span>
                      {row.startTime ? (
                        <span className="text-xs text-gray-600 dark:text-slate-400">
                          {formatTime(row.startTime)}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                      {row.service?.name || "Serviço"}
                      {row.clientCar
                        ? ` · ${row.clientCar.model} (${row.clientCar.plate})`
                        : ""}
                    </p>
                    <p className="text-xs sm:text-sm font-semibold text-red-700 dark:text-red-400 mt-1">
                      {formatBalanceCurrency(parsePrice(row.price))}
                    </p>
                  </BalanceUnpaidRow>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};
